import type { Vote } from '@filmsloth/shared';
import { brandedKey } from './brandedKey.js';

/**
 * Votes that were cast but have not reached the server yet.
 *
 * A phone loses its connection constantly — a lift, a basement flat, the
 * three metres of hallway with no signal — and until now a vote cast in one
 * of those moments was simply gone: the card left the screen (the optimistic
 * update) and the request failed, so the film stayed unanswered for the whole
 * group while the person who voted had every reason to think they had.
 *
 * So a failed vote is kept here instead of dropped, and sent again when the
 * connection comes back. Three properties make that safe rather than
 * reckless:
 *
 * - The server treats a repeated identical vote as a no-op, so sending one
 *   twice cannot flip a decision or double-count anything.
 * - Only failures that *might* succeed later are queued. A refusal with a
 *   reason — the session is gone, the film is not in the deck — is an answer,
 *   and retrying it forever would be a lie told on a timer.
 * - Entries expire. A session is swept after six hours of inactivity, so a
 *   vote older than that has nowhere to arrive; it is dropped rather than
 *   resurrected into whatever session happens to be running.
 */
export interface PendingVote {
  movieId: string;
  vote: Vote;
  /** When it was cast, in epoch milliseconds. */
  at: number;
}

/** Matches the server's own six-hour sweep of idle sessions. */
export const PENDING_VOTE_MAX_AGE_MS = 6 * 60 * 60 * 1000;

/**
 * More than this and something is wrong anyway — nobody swipes two hundred
 * cards in a tunnel. The cap stops a broken connection from filling storage.
 */
export const PENDING_VOTE_LIMIT = 200;

const STORAGE_KEY = brandedKey('pendingVotes');

/**
 * Adds a vote, keeping the first answer for a film.
 *
 * A vote cannot be changed once it is in: the server would refuse the second
 * one. Keeping the first is therefore not just a tie-break, it is the only
 * answer that matches what the group will eventually see.
 */
export function addPendingVote(votes: readonly PendingVote[], entry: PendingVote): PendingVote[] {
  if (votes.some((pending) => pending.movieId === entry.movieId)) return [...votes];
  return [...votes, entry].slice(-PENDING_VOTE_LIMIT);
}

export function removePendingVote(votes: readonly PendingVote[], movieId: string): PendingVote[] {
  return votes.filter((pending) => pending.movieId !== movieId);
}

export function prunePendingVotes(
  votes: readonly PendingVote[],
  now: number,
  maxAgeMs = PENDING_VOTE_MAX_AGE_MS,
): PendingVote[] {
  return votes.filter((pending) => now - pending.at < maxAgeMs);
}

interface StoredQueue {
  /** The session the votes belong to; a queue never crosses sessions. */
  code: string;
  votes: PendingVote[];
}

function isVote(value: unknown): value is Vote {
  return value === 'like' || value === 'dislike' || value === 'neutral';
}

/** Anything unreadable is treated as an empty queue, never as an error. */
export function loadPendingVotes(code: string, now = Date.now()): PendingVote[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw) as Partial<StoredQueue>;
    // A queue from a different session is not ours to send; the codes are
    // what keeps a leftover from last night out of tonight's deck.
    if (parsed.code !== code || !Array.isArray(parsed.votes)) return [];
    const votes = parsed.votes.filter(
      (entry): entry is PendingVote =>
        typeof entry?.movieId === 'string' && isVote(entry.vote) && typeof entry.at === 'number',
    );
    return prunePendingVotes(votes, now);
  } catch {
    return [];
  }
}

export function savePendingVotes(code: string, votes: readonly PendingVote[]): void {
  try {
    if (votes.length === 0) {
      window.localStorage.removeItem(STORAGE_KEY);
      return;
    }
    const stored: StoredQueue = { code, votes: [...votes] };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Storage is unavailable or full. The queue still works for this tab,
    // which covers the case it exists for.
  }
}

export function clearPendingVotes(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing depends on the removal succeeding.
  }
}

/**
 * Whether a failed request is worth trying again.
 *
 * The distinction this draws is the whole feature: a request that never
 * reached anybody (no network, a dead gateway, a timeout) is worth keeping,
 * and a request that was answered with "no" is not — retrying that is a
 * promise the app cannot keep.
 */
export function isRetryable(status: number | null): boolean {
  // `null` means fetch itself rejected: no response at all.
  if (status === null) return true;
  // 408 and 429 ask for exactly that, and 5xx is the server having a moment.
  if (status === 408 || status === 429) return true;
  return status >= 500;
}
